import type { Ref } from 'vue'
import type {
  NotificationInboxLinks,
  NotificationInboxQuery,
  NotificationInboxResponse
} from '~/services/api'
import { useApiClient } from '~/services/api'
import { useLogger } from '~/composables/useLogger'
import { Normalization } from '~/utils/normalization'
import { identityGeneration } from '~/utils/identityGeneration'
import { useNotificationsStore } from '~/stores/useNotificationsStore'

const DEFAULT_INBOX_QUERY: NotificationInboxQuery = {
  per_page: 10
}

const extractNextPage = (links?: Partial<NotificationInboxLinks> | null) => {
  const nextUrl = links?.next

  if (typeof nextUrl === 'string' && nextUrl.length > 0) {
    try {
      const url = new URL(nextUrl, window.location.origin)
      const page = Normalization.intValue(url.searchParams.get('page'), null)

      return page !== null && page > 0 ? page : null
    } catch {
      return null
    }
  }

  return null
}

/** Loads the notification inbox (first page and subsequent pages) into the notifications store. */
export const useNotificationsInbox = (isAuthenticated: Readonly<Ref<boolean>>) => {
  const logger = useLogger('useNotificationsInbox')
  const notificationsStore = useNotificationsStore()
  const { notifications: notificationsApi } = useApiClient()

  const syncInboxPage = (
    response: NotificationInboxResponse,
    mode: 'replace' | 'append' = 'replace'
  ) => {
    const items = response.data

    if (mode === 'append') {
      notificationsStore.appendItems(items)
    } else {
      notificationsStore.replaceItems(items)
    }

    notificationsStore.setPagination(response.links, response.meta)
  }

  const loadInitial = async (
    query: NotificationInboxQuery = DEFAULT_INBOX_QUERY,
    force = false
  ) => {
    if (!isAuthenticated.value) {
      return
    }

    if (notificationsStore.isLoaded && !force) {
      return
    }

    const generation = identityGeneration.current()
    notificationsStore.setError(null)

    const [inboxResult, unreadCountResult] = await Promise.allSettled([
      notificationsApi().getInbox(query),
      notificationsApi().getUnreadCount()
    ])
    if (!identityGeneration.isCurrent(generation)) return

    if (inboxResult.status === 'fulfilled') {
      syncInboxPage(inboxResult.value, 'replace')
    }

    if (unreadCountResult.status === 'fulfilled') {
      notificationsStore.setUnreadCount(unreadCountResult.value.data.unread_count)
    }

    if (inboxResult.status === 'rejected' && unreadCountResult.status === 'rejected') {
      notificationsStore.setError('Unable to load notifications right now.')
    }

    notificationsStore.setLoaded(
      inboxResult.status === 'fulfilled' || unreadCountResult.status === 'fulfilled'
    )
  }

  const loadMore = async () => {
    if (
      !isAuthenticated.value ||
      notificationsStore.isFetchingMore ||
      !notificationsStore.hasNextPage
    ) {
      return false
    }

    const nextPage = extractNextPage(notificationsStore.paginationLinks)

    if (nextPage == null || nextPage <= 0) {
      notificationsStore.setPagination(
        {
          ...notificationsStore.paginationLinks,
          next: null
        },
        notificationsStore.paginationMeta
      )
      return false
    }

    const generation = identityGeneration.current()
    notificationsStore.setFetchingMore(true)

    try {
      const perPage = Normalization.intValue(notificationsStore.paginationMeta?.per_page, null)
      const response = await notificationsApi().getInbox({
        per_page: perPage !== null && perPage > 0 ? perPage : (DEFAULT_INBOX_QUERY.per_page ?? 10),
        page: nextPage
      })
      if (!identityGeneration.isCurrent(generation)) return false

      syncInboxPage(response, 'append')
      return true
    } catch (error) {
      if (!identityGeneration.isCurrent(generation)) return false
      notificationsStore.setError('Unable to load more notifications right now.')
      logger.error('loadMore', error)
      return false
    } finally {
      if (identityGeneration.isCurrent(generation)) notificationsStore.setFetchingMore(false)
    }
  }

  return { loadInitial, loadMore }
}
